import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import {
 Flex,
 Box,
 Text,
 Switch,
 Button,
 Badge,
 Heading,
 useToast,
 SkeletonText,
 Alert,
 AlertIcon,
} from '@chakra-ui/react';
import { MainContent, PageHeaderStructure } from '../../components/Layout';
import { Select } from '../../components/Input';
import baseService from '../../services/base';
import { fetchData } from '../../services/fetch';

const SectionCard = ({ children, ...rest }) => (
 <Box
  bg='white'
  borderRadius='12px'
  border='1px solid #E5E5E5'
  p={{ base: '24px', xl: '32px' }}
  {...rest}
 >
  {children}
 </Box>
);

// EG-27: idiomas do sistema (pt-BR padrão + es). Estrutura pronta p/ EN entrar.
const ESG_LANGUAGES = [
 { code: 'pt-BR', label: 'Português (BR)', isDefault: true },
 { code: 'es', label: 'Espanhol (ES)', isDefault: false },
];

const EsgStarter = () => {
 const toast = useToast();
 const navigate = useNavigate();

 const { data: setting, isLoading: loadingSetting, mutate } = useSWR(
  '/esg-starter',
  fetchData
 );
 const { data: templates, isLoading: loadingTemplates } = useSWR(
  '/survey-template/all',
  fetchData
 );

 // Config por idioma: { 'pt-BR': { enabled, templateId }, 'es': {...} }.
 const [langConfig, setLangConfig] = useState({});
 const [saving, setSaving] = useState(false);

 useEffect(() => {
  if (!setting) return;
  const map = setting.templatesByLanguage || {};
  const cfg = {};
  ESG_LANGUAGES.forEach(({ code }) => {
   const v = map[code];
   cfg[code] = {
    enabled: v ? !!v.enabled : false,
    templateId:
     v && v.id_survey_template != null ? String(v.id_survey_template) : '',
   };
  });
  setLangConfig(cfg);
 }, [setting]);

 const templateOptions = useMemo(
  () =>
   (templates || []).map((template) => ({
    value: template.id_survey_template,
    label:
     template.name +
     (template?.meta?.totalQuestions != null
      ? ` (${template.meta.totalQuestions} perguntas)`
      : ''),
   })),
  [templates]
 );

 const findTemplate = (id) =>
  (templates || []).find((t) => String(t.id_survey_template) === String(id));

 const setLang = (code, patch) =>
  setLangConfig((prev) => ({ ...prev, [code]: { ...prev[code], ...patch } }));

 const handleSave = async () => {
  // Sem fallback: cada idioma reage ao seu próprio template. Idioma ativo
  // precisa do seu template (senão não geraria nada).
  const enabledMissingTemplate = ESG_LANGUAGES.some(
   ({ code }) => langConfig[code]?.enabled && !langConfig[code]?.templateId
  );
  if (enabledMissingTemplate) {
   toast({
    title: 'Idioma ativo precisa de um template selecionado.',
    status: 'warning',
    duration: 6000,
    isClosable: true,
   });
   return;
  }

  try {
   setSaving(true);
   const templatesPayload = {};
   ESG_LANGUAGES.forEach(({ code }) => {
    const c = langConfig[code] || {};
    templatesPayload[code] = {
     enabled: !!c.enabled,
     id_survey_template: c.templateId ? Number(c.templateId) : null,
    };
   });
   await baseService.patch('/esg-starter', {
    templates: templatesPayload,
   });
   await mutate();
   toast({
    title: 'Configuração do ESG Starter salva',
    status: 'success',
    duration: 4000,
    isClosable: true,
   });
  } catch (err) {
   toast({
    title: 'Não foi possível salvar a configuração',
    status: 'error',
    duration: 5000,
    isClosable: true,
   });
  } finally {
   setSaving(false);
  }
 };

 const isLoading = loadingSetting || loadingTemplates;

 return (
  <MainContent>
   <PageHeaderStructure
    title='ESG Starter'
    subtitle='Diagnóstico reduzido padrão liberado automaticamente para toda empresa que se auto-cadastra, mesmo sem a compra de certificação.'
   >
    <Button
     variant='primary'
     onClick={handleSave}
     isLoading={saving}
     isDisabled={isLoading}
     minW='220px'
     h='52px'
    >
     Salvar configuração
    </Button>
   </PageHeaderStructure>

   {isLoading ? (
    <SectionCard mt='24px'>
     <SkeletonText noOfLines={5} spacing='4' />
    </SectionCard>
   ) : (
    <Flex flexDir='column' gap='24px' mt='24px' maxW='760px'>
     <Alert status='info' borderRadius='12px' bg='#E7F1FA' color='#0A4D7A'>
      <AlertIcon color='#0073BB' />
      Lembre-se de salvar a configuração sempre que alterar algo.
     </Alert>

     {/* Configuração por idioma */}
     <Box>
      <Heading fontSize='18px' mb='6px'>
       Configuração por idioma
      </Heading>
      <Text fontSize='sm' color='#5C6066' mb='16px'>
       Cada idioma gera o diagnóstico apenas para empresas cadastradas nesse
       idioma, usando o template escolhido. Idiomas desativados ou sem template
       não geram nada. Alterações valem apenas para os próximos cadastros.
      </Text>

      <Flex flexDir='column' gap='16px'>
       {ESG_LANGUAGES.map(({ code, label, flag, isDefault }) => {
        const c = langConfig[code] || { enabled: false, templateId: '' };
        const selected = findTemplate(c.templateId);
        return (
         <Box
          key={code}
          bg='white'
          borderRadius='12px'
          border='1px solid #E5E5E5'
          borderLeft={`4px solid ${c.enabled ? '#0073BB' : '#D7DBE0'}`}
          p='20px'
          transition='0.15s'
         >
          <Flex justify='space-between' align='center' gap='12px' mb='16px'>
           <Flex align='center' gap='10px' wrap='wrap'>
            <Text fontSize='20px' lineHeight='1'>
             {flag}
            </Text>
            <Heading fontSize='16px'>{label}</Heading>
            {isDefault && (
             <Badge
              bg='#E7F1FA'
              color='#0073BB'
              borderRadius='full'
              px='8px'
              py='1px'
              fontSize='10px'
              textTransform='none'
             >
              Padrão
             </Badge>
            )}
            <Badge
             borderRadius='full'
             px='8px'
             py='1px'
             fontSize='10px'
             textTransform='none'
             color={c.enabled ? '#1F7A33' : '#5C6066'}
             bg={c.enabled ? '#E3F5E8' : '#EDEFF2'}
            >
             {c.enabled ? 'Ativo' : 'Inativo'}
            </Badge>
           </Flex>
           <Switch
            colorScheme='blue'
            isChecked={c.enabled}
            onChange={(e) => setLang(code, { enabled: e.target.checked })}
           />
          </Flex>

          <Box opacity={c.enabled ? 1 : 0.6}>
           <Select
            name={`esg-template-${code}`}
            label='TEMPLATE'
            placeholder='Selecione um template'
            value={c.templateId || ''}
            options={templateOptions}
            onChange={(e) => setLang(code, { templateId: e.target.value })}
           />

           {selected && (
            <Flex
             mt='12px'
             p='12px 16px'
             bg='#F1F5FA'
             borderRadius='10px'
             align='center'
             justify='space-between'
             gap='16px'
             wrap='wrap'
            >
             <Box>
              <Text fontWeight={600} color='#292B30'>
               {selected.name}
              </Text>
              <Text fontSize='sm' color='#5C6066'>
               {selected?.meta?.totalQuestions ?? 0} pergunta
               {selected?.meta?.totalQuestions === 1 ? '' : 's'}
              </Text>
             </Box>
             <Button
              variant='secondary'
              size='sm'
              h='40px'
              onClick={() => navigate(`/templates/${c.templateId}`)}
             >
              Visualizar / editar
             </Button>
            </Flex>
           )}
          </Box>
         </Box>
        );
       })}
      </Flex>

      <Button
       variant='link'
       color='#0073BB'
       fontSize='sm'
       mt='16px'
       onClick={() => navigate('/templates/create')}
      >
       + Criar novo template
      </Button>
     </Box>
    </Flex>
   )}
  </MainContent>
 );
};

export default EsgStarter;
